import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { API_BASE, ApiError, bindSession, type Schemas } from '../api/client';

type Tokens = Schemas['AuthTokens'];
/** GET /me. `hiddenMenus` is missing from a /me a desktop kept from before menus existed. */
export type Me = Omit<Schemas['Me'], 'hiddenMenus'> & Partial<Pick<Schemas['Me'], 'hiddenMenus'>>;

/**
 * Session: the access token lives in memory only; the refresh token and shop slug in localStorage so a
 * reload keeps the user signed in (refresh rotates on every use). Phase 2 note: an HttpOnly-cookie
 * refresh mode is planned on the backend (PROJECT_STATUS deferred items).
 */
const STORAGE_KEY = 'sm.session';

interface Stored {
  refreshToken: string;
  shopSlug: string;
}

function readStored(): Stored | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : null;
  } catch {
    return null;
  }
}

function writeStored(value: Stored | null) {
  try {
    if (value) localStorage.setItem(STORAGE_KEY, JSON.stringify(value));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* storage unavailable: session lasts for this tab only */
  }
}

async function post<T>(path: string, body: unknown, token?: string): Promise<T> {
  const res = await fetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Idempotency-Key': crypto.randomUUID(),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(body),
  });
  if (!res.ok) throw await ApiError.from(res);
  return (res.status === 204 ? undefined : await res.json()) as T;
}

export type AuthStatus = 'loading' | 'signedOut' | 'signedIn';

interface AuthContextValue {
  status: AuthStatus;
  me: Me | null;
  can: (...permissions: string[]) => boolean;
  login: (input: Schemas['LoginRequest']) => Promise<void>;
  register: (input: Schemas['RegisterShopRequest']) => Promise<void>;
  /** Creates a new shop from a checked backup (see RegisterPage) and signs its owner in. */
  restore: (input: Schemas['RestoreShopRequest']) => Promise<void>;
  logout: () => Promise<void>;
  reloadMe: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const access = useRef<string | null>(null);
  const stored = useRef<Stored | null>(readStored());
  const [status, setStatus] = useState<AuthStatus>(() => (readStored() ? 'loading' : 'signedOut'));
  const [me, setMe] = useState<Me | null>(null);

  const accept = useCallback((tokens: Tokens, shopSlug: string) => {
    access.current = tokens.accessToken;
    stored.current = { refreshToken: tokens.refreshToken, shopSlug };
    writeStored(stored.current);
  }, []);

  const clear = useCallback(() => {
    access.current = null;
    stored.current = null;
    writeStored(null);
    setMe(null);
    setStatus('signedOut');
    queryClient.clear();
  }, [queryClient]);

  // Refresh tokens rotate on every use, so a second refresh with the same token would be treated
  // as reuse and end the session. Concurrent callers (StrictMode's double effects, parallel 401s)
  // share one request.
  const inflight = useRef<Promise<string | null> | null>(null);
  const refresh = useCallback((): Promise<string | null> => {
    const current = stored.current;
    if (!current) return Promise.resolve(null);
    inflight.current ??= post<Tokens>('/auth/refresh', { refreshToken: current.refreshToken })
      .then((tokens) => {
        accept(tokens, current.shopSlug);
        return tokens.accessToken;
      })
      .catch(() => null)
      .finally(() => {
        inflight.current = null;
      });
    return inflight.current;
  }, [accept]);

  const loadMe = useCallback(async () => {
    const res = await fetch(`${API_BASE}/me`, {
      headers: { Authorization: `Bearer ${access.current ?? ''}` },
    });
    if (!res.ok) throw await ApiError.from(res);
    setMe((await res.json()) as Me);
    setStatus('signedIn');
  }, []);

  useEffect(() => {
    bindSession({ accessToken: () => access.current, refresh, onSessionExpired: clear });
  }, [refresh, clear]);

  // Resume a stored session once on start.
  useEffect(() => {
    if (!stored.current) return;
    void (async () => {
      const token = await refresh();
      if (!token) {
        clear();
        return;
      }
      await loadMe().catch(clear);
    })();
  }, [refresh, loadMe, clear]);

  const value = useMemo<AuthContextValue>(() => {
    const permissions = new Set(me?.permissions ?? []);
    return {
      status,
      me,
      can: (...keys) => keys.every((k) => permissions.has(k)),
      login: async (input) => {
        const tokens = await post<Tokens>('/auth/login', input);
        accept(tokens, input.shopSlug);
        await loadMe();
      },
      register: async (input) => {
        const res = await post<Schemas['RegisterShopResponse']>('/auth/register-shop', input);
        accept(res.tokens, res.tenant.slug);
        await loadMe();
      },
      restore: async (input) => {
        const res = await post<Schemas['RegisterShopResponse']>('/auth/restore-shop', input);
        accept(res.tokens, res.tenant.slug);
        await loadMe();
      },
      logout: async () => {
        const current = stored.current;
        if (current) {
          await post(
            '/auth/logout',
            { refreshToken: current.refreshToken },
            access.current ?? undefined,
          ).catch(() => undefined);
        }
        clear();
      },
      reloadMe: loadMe,
    };
  }, [status, me, accept, loadMe, clear]);

  return <AuthContext value={value}>{children}</AuthContext>;
}

export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
